import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { toast, ToastContainer } from "react-toastify";
import {
  buildEndUserPaymentPaidBy,
  type PaymentPaidByPayload,
  type PaymentPaidBySource,
} from "../utils/buildPaymentPaidBy";

export type PublicPaymentActor = PaymentPaidBySource;

const ZERO_ACTOR_ID = "000000000000000000000000";

function isUsablePaymentActorId(id: string): boolean {
  const s = String(id || "").trim();
  return Boolean(s) && s !== ZERO_ACTOR_ID;
}

/** Resolve paid_by for public payment POST bodies (sales, installation, removal). */
export function buildPublicPaymentPaidBy(
  paidBy?: PublicPaymentActor,
  createdBy?: PublicPaymentActor,
  supplierId?: string
): PaymentPaidByPayload {
  const fromPaidBy = buildEndUserPaymentPaidBy(paidBy || {});
  const hasEndUserIds = Boolean(
    fromPaidBy.membership_id || fromPaidBy.global_user_id || fromPaidBy.organization_id
  );
  if (hasEndUserIds || (fromPaidBy.name && fromPaidBy.name !== "Customer")) {
    return fromPaidBy;
  }

  // Fallback: created_by / supplier org when end-user ids are missing on the order.
  const fallbackMembership =
    String(createdBy?.membership_id || createdBy?.id || "").trim();
  const fallbackOrg =
    String(createdBy?.organization_id || supplierId || "").trim();
  return buildEndUserPaymentPaidBy({
    name: createdBy?.name || paidBy?.name || "Unknown User",
    membership_id: isUsablePaymentActorId(fallbackMembership) ? fallbackMembership : "",
    global_user_id: createdBy?.global_user_id,
    organization_id: isUsablePaymentActorId(fallbackOrg) ? fallbackOrg : "",
  });
}

export type PublicPaymentsProps = {
  supplierId: string;
  /** Signage supplier user id for post-payment email branding (prefer URL `supplier_id`, not project id). */
  emailBrandingSupplierId?: string;
  totalAmount: number;
  dueAmount: number;
  orderId: string;
  projectId?: string;
  publicToken?: string;
  selectedPaymentMethod: string;
  /** Order creator (e.g. signage user) — forwarded to payment POST body. */
  createdBy?: PublicPaymentActor;
  /** Payer — for public sales orders, typically the buyer/customer (foundation ids). */
  paidBy?: PublicPaymentActor;
  onPaymentSuccess: (paymentIntentId: string) => void;
};

// projects/${encodeURIComponent(projectId)}

/** PUT `/public/projects/{project_id}/orders/{order_id}/payment?token=` — records payment on the order after Stripe succeeds. */
const buildPublicOrderPaymentUrl = ( orderId: string,) =>
  `${process.env.REACT_APP_API_URL}/public/orders/${encodeURIComponent(orderId)}/payment`;

/**
 * Public order summary payment: PaymentElement + confirmPayment against an existing
 * PaymentIntent (client_secret supplied via Elements parent in Order_summary).
 */
const PublicPayments: React.FC<PublicPaymentsProps> = ({
  supplierId,
  emailBrandingSupplierId,
  totalAmount,
  dueAmount,
  orderId,
  projectId,
  publicToken,
  selectedPaymentMethod,
  createdBy,
  paidBy,
  onPaymentSuccess,
}) => {

//   console.log("emailBrandingSupplierId", emailBrandingSupplierId || "not found");
// console.log("supplierId", supplierId || "not found");
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const paymentElementScrollStyle: React.CSSProperties = {
    maxHeight: "min(52vh, 420px)",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    paddingRight: 4,
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      // if (!projectId || !publicToken) {
      //   throw new Error("Missing project or access token for public payment update.");
      // }
      const paymentUpdateUrl = buildPublicOrderPaymentUrl(orderId);
      const paidByPayload = buildPublicPaymentPaidBy(paidBy, createdBy, supplierId);
      const response = await fetch(paymentUpdateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          payment_id: paymentIntentId,
          total_amount: Math.round(Number(totalAmount) * 100) / 100,
          amount_paid: Math.round(Number(totalAmount) * 100) / 100,
          amount_due: dueAmount,
          payment_date: new Date().toISOString(),
          method: selectedPaymentMethod,
          // created_by: createdByPayload,
          paid_by: paidByPayload,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update payment status in order");
      }

      await response.json().catch(() => ({}));
      localStorage.removeItem("cart_id");

      onPaymentSuccess(paymentIntentId);
    } catch (error) {
      const errMessage = (error as Error).message || "An unknown error occurred";
      console.error("Payment Update Failed:", errMessage);
      toast.error(`Error: ${errMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.error("Stripe is not ready");
      return;
    }

    setLoading(true);
    try {
      await elements.submit();
      const paymentIntentRes = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (paymentIntentRes.error) {
        throw new Error(paymentIntentRes.error.message || "Payment failed");
      }

      const pi = paymentIntentRes.paymentIntent;
      if (!pi?.id) {
        throw new Error("Payment did not complete");
      }

      await handlePaymentSuccess(pi.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Payment failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <ToastContainer />
      <form onSubmit={handleSubmit}>
        <div className="mb-3" style={paymentElementScrollStyle}>
          <PaymentElement
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
            }}
          />
        </div>
        <button type="submit" className="btn btn-primary w-100 fw-bold" disabled={!stripe || loading}>
          {loading ? "Processing..." : `Pay $${totalAmount.toFixed(2)}`}
        </button>
      </form>
    </div>
  );
};

export default PublicPayments;
